//NEW: UI_REVAMP
import { Box, Divider, Flex, Grid, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useBetTwo3DStore } from '../../../stores/useBetTwo3DStore';
import { useGlobalToast } from '../../../utils/ShowToast';
import SelectionButton from '../../button/SelectionButton';
import './bet2.css';
import HotNumberText from './HotNumberText';

const NUMBER_RANGES = [
	{ start: 0, end: 99 },
	{ start: 100, end: 199 },
	{ start: 200, end: 299 },
	{ start: 300, end: 399 },
	{ start: 400, end: 499 },
	{ start: 500, end: 599 },
	{ start: 600, end: 699 },
	{ start: 700, end: 799 },
	{ start: 800, end: 899 },
	{ start: 900, end: 999 },
];

export const BetTwo3DKeypad = ({
	onAddToList,
	betting = [],
	removeBetting,
	removeAllByDigit,
	gameId,
	drawId,
	activeBetType,
	onManualInteract,
	isDisabled,
}) => {
	const isBetTypeEmpty = activeBetType === null;
	const [selectedRange, setSelectedRange] = useState(NUMBER_RANGES[0]);
	const { addSelectedNumbers, removeSelectedNumber, selectedNumbers, hotNumbers, soldOutNumbers } =
		useBetTwo3DStore();

	const globalShowToast = useGlobalToast();

	const getExistingBet = (num) => {
		const digit = num.toString().padStart(3, '0');
		const betType = activeBetType ? 'Ramble' : 'Straight';
		// Check across ALL tabs (shared list)
		return (betting || []).find(
			(b) =>
				b.gameId === gameId && b.drawId === drawId && b.digit === digit && b.betType === betType,
		);
	};

	const handleNumberClick = (number) => {
		if (isSoldOut(number)) return;

		onManualInteract?.();
		const existingBet = getExistingBet(number);
		if (existingBet) {
			// Remove ALL bets with this digit from the shared list
			const digit = number.toString().padStart(3, '0');
			if (removeAllByDigit) {
				removeAllByDigit(digit);
			} else {
				removeBetting(existingBet.id);
			}
			return;
		}

		if (selectedNumbers.includes(number)) {
			removeSelectedNumber(number);
		} else {
			addSelectedNumbers([number]);
			setTimeout(() => {
				onAddToList();
			}, 100);
		}
	};

	const numbers = useMemo(() => {
		const { start, end } = selectedRange;
		return Array.from({ length: end - start + 1 }, (_, i) => start + i);
	}, [selectedRange]);

	// 判断是否为热门号码
	const isHotNumber = (num) => {
		const formatted = num.toString().padStart(3, '0').split('').join(',');
		return hotNumbers.includes(formatted);
	};

	const isSoldOut = (num) => {
		const formatted = num.toString().padStart(3, '0').split('').join(',');
		return soldOutNumbers.includes(formatted);
	};

	return (
		<Flex w='100%' h='100%' minH='0'>
			<Flex
				className='quick-pick-container'
				direction='column'
				gap={2}
				px={2}
				py={1}
				h={{ base: '100%', lg: 'auto' }}
				maxH={{ base: '100%', lg: 'calc(100% - 2px)' }}
				alignSelf={{ base: 'stretch', lg: 'flex-start' }}
				mb={{ base: 0, lg: 4 }}
				width={{ base: 'max-content', lg: '150px' }}
				overflowY='auto'
			>
				<Text
					fontWeight={500}
					px={1}
					fontSize={{ base: '12px', lg: '16px' }}
					color={{ base: '#5E5D61', lg: '#3B3B40' }}
				>
					Quick Pick
				</Text>
				<Divider borderColor='#457CEE' mt={-1} display={{ base: 'block', lg: 'none' }} />
				<Box
					flex='1'
					minH='0'
					overflowY='auto'
					gap={2}
					display='flex'
					flexDirection='column'
					pb={3}
				>
					{NUMBER_RANGES.map((range, index) => {
						let state = 'default';

						if (isDisabled) {
							state = 'disabled';
						} else if (range === selectedRange) {
							state = 'selected';
						} else {
							state = 'default';
						}
						return (
							<SelectionButton
								key={`${range.start}-${range.end}-${index}`}
								variant='quickPick'
								onClick={() => setSelectedRange(range)}
								status={state}
							>
								<Text
									fontSize={{ base: '14px', lg: '16px' }}
									fontWeight={range === selectedRange ? 700 : 600}
									opacity={!isDisabled ? 1 : 0.5}
								>
									{range.start.toString().padStart(3, '0')} -{' '}
									{range.end.toString().padStart(3, '0')}
								</Text>
							</SelectionButton>
						);
					})}
				</Box>
			</Flex>
			<Flex direction='column' px={2} pt={1} pb={0.5} flex='1' minH='0'>
				<Text
					fontWeight={500}
					fontSize={{ base: '12px', lg: '16px' }}
					color={{ base: '#5E5D61', lg: '#3B3B40' }}
				>
					Numbers
				</Text>
				<Box className='numbers-container' flex='1' minH='0' overflowY='auto' mt={2} pb={3}>
					<Grid
						templateColumns={{
							base: 'repeat(3, 1fr)',
							lg: 'repeat(5, 1fr)',
						}}
						gap={2}
					>
						{numbers.map((num) => {
							const isSelected = selectedNumbers.includes(num) || !!getExistingBet(num);
							const soldOut = isSoldOut(num);
							const isHot = isHotNumber(num) && !soldOut && !isBetTypeEmpty;

							const isSameDigits = (n) => {
								const s = n.toString().padStart(3, '0');
								return s[0] === s[1] && s[1] === s[2];
							};
							const isRambleDisabled = activeBetType && isSameDigits(num);

							let state = 'default';
							if (isDisabled || isRambleDisabled) state = 'disabled';
							else if (soldOut && !isBetTypeEmpty) state = 'soldOut';
							else if (isSelected && !isBetTypeEmpty) state = 'selected';
							else if (isBetTypeEmpty) state = 'default';
							else state = 'default';

							return (
								<SelectionButton
									key={num}
									variant='numberPick'
									status={state}
									isBetTypeEmpty={isBetTypeEmpty}
									onClick={() => {
										if (isBetTypeEmpty) {
											globalShowToast({
												title: 'Please select Straight or Ramble',
												status: 'warning',
												position: 'top',
											});
											return;
										}
										if (!isDisabled && !soldOut && !isRambleDisabled) {
											handleNumberClick(num);
										}
									}}
								>
									<HotNumberText
										isHot={isHot}
										isSelected={isSelected}
										num={num.toString().padStart(3, '0')}
									/>
								</SelectionButton>
							);
						})}
					</Grid>
				</Box>
			</Flex>
		</Flex>
	);
};
